import dayjs from 'dayjs';
import sumBy from 'lodash/sumBy';

import {
  SecurityInsightsContainerImagesResponse,
  SecurityInsightsVulnerabilitiesTrendResponse,
} from '@cast/types';

export const generateSecurityInsightsVulnerabilitiesTrendJson = ({
  imagesData,
  startTime,
  endTime,
  stepSeconds,
}: {
  imagesData: SecurityInsightsContainerImagesResponse;
  startTime?: string;
  endTime?: string;
  stepSeconds?: number;
}): SecurityInsightsVulnerabilitiesTrendResponse => {
  const now = dayjs();
  const start = startTime ? dayjs(startTime) : now.subtract(30, 'days');
  const end = endTime ? dayjs(endTime) : now;

  // Default step is 1 day if not provided
  const step = stepSeconds || 86400;
  const stepDays = step / 86400;

  // Calculate number of data points to generate
  const diffDays = end.diff(start, 'day');
  const dataPoints = Math.floor(diffDays / stepDays) + 1;

  // Calculate current state from images data
  const currentCritical = sumBy(
    imagesData.items,
    (img) => img.vulnerabilitiesBySeverityLevel?.critical ?? 0
  );
  const currentHigh = sumBy(
    imagesData.items,
    (img) => img.vulnerabilitiesBySeverityLevel?.high ?? 0
  );
  const currentMedium = sumBy(
    imagesData.items,
    (img) => img.vulnerabilitiesBySeverityLevel?.medium ?? 0
  );
  const currentLow = sumBy(
    imagesData.items,
    (img) => img.vulnerabilitiesBySeverityLevel?.low ?? 0
  );
  const currentNone = sumBy(
    imagesData.items,
    (img) => img.vulnerabilitiesBySeverityLevel?.none ?? 0
  );
  const currentTotal =
    currentCritical + currentHigh + currentMedium + currentLow + currentNone;

  const clustersCount = Math.max(
    ...(imagesData.items ?? []).map((img) => img.clusters ?? 0)
  );
  const workloadsCount = sumBy(
    imagesData.items,
    (img) => img.affectedResources ?? 0
  );

  // Generate timeseries data with decreasing trend
  // Start with 50% more vulnerabilities than current state
  const timeseries = [];
  const startTotal = Math.round(currentTotal * 1.5);
  const endTotal = currentTotal;
  const decrement = (startTotal - endTotal) / (dataPoints - 1);

  for (let i = 0; i < dataPoints; i++) {
    const timestamp = start
      .add(i * stepDays, 'day')
      .startOf('day')
      .toISOString();
    const total = Math.round(startTotal - decrement * i);

    // Calculate proportional distribution based on current state ratios
    const ratio = currentTotal > 0 ? total / currentTotal : 1;
    const critical = Math.round(currentCritical * ratio);
    const high = Math.round(currentHigh * ratio);
    const medium = Math.round(currentMedium * ratio);
    const low = Math.round(currentLow * ratio);
    const none = Math.round(currentNone * ratio);

    // Adjust to ensure sum equals total
    const sum = critical + high + medium + low + none;
    const diff = total - sum;
    const adjustedMedium = medium + diff; // Add any difference to medium

    timeseries.push({
      time: timestamp,
      vulnsTotal: total,
      vulnsCritical: critical,
      vulnsHigh: high,
      vulnsMedium: adjustedMedium,
      vulnsLow: low,
      vulnsNone: none,
      clustersCount,
      workloadsCount,
    });
  }

  return {
    timeseries,
  };
};
